// src/components/account/AccountField.jsx
import React from "react";
import ShopSelect from "../ShopSelect";

export default function AccountField({
  label,
  value,
  onChange,
  onKeyDown,
  placeholder,
  type = "text",
  inputMode,
  maxLength,
  error,
  helper,
  isDark,

  // ✅ add this to fix eslint no-undef
  options = [],

  readOnly,
  disabled,

  actionLabel,
  onAction,
  actionDisabled,
  actionLoading,

  secondaryActionLabel,
  onSecondaryAction,
  secondaryActionDisabled,

  onControlClick,
  controlIcon,
  controlAriaHaspopup,
  controlAriaExpanded,
}) {
  const border = error
    ? "border-red-200 dark:border-red-900/60"
    : "border-neutral-200 dark:border-neutral-800";
  const focusRing = error
    ? "focus-within:border-red-400 focus-within:ring-2 focus-within:ring-red-500/10 dark:focus-within:border-red-500 dark:focus-within:ring-red-500/20"
    : "focus-within:border-neutral-400 focus-within:ring-2 focus-within:ring-neutral-900/10 dark:focus-within:border-neutral-600 dark:focus-within:ring-neutral-500/10";

  const containerBg = "bg-white dark:bg-neutral-900";
  const inputText = "text-neutral-900 dark:text-neutral-50";
  const placeholderText = "placeholder:text-neutral-400 dark:placeholder:text-neutral-500";
  const arrowColor = "text-neutral-400 dark:text-neutral-500";

  const helperColor = error
    ? "text-red-600 dark:text-red-300"
    : "text-neutral-500 dark:text-neutral-400";

  const btnBase =
    "text-[10px] font-semibold tracking-[0.25em] px-3 py-2 rounded-xl transition";
  const btnPrimary =
    "bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-neutral-50 dark:text-neutral-900 dark:hover:bg-neutral-200";
  const btnGhost =
    "border border-neutral-200 hover:bg-neutral-50 dark:border-neutral-800 dark:hover:bg-neutral-800";

  const isSelect = type === "select";
  const canUseSelect = isSelect && Array.isArray(options) && options.length > 0;
  const inputPadRight = secondaryActionLabel ? "pr-40" : actionLabel ? "pr-24" : "pr-4";
  const selectPadRight = secondaryActionLabel ? "pr-48" : actionLabel ? "pr-32" : "pr-10";
  const selectArrowRightClass = secondaryActionLabel
    ? "right-40"
    : actionLabel
    ? "right-24"
    : "right-3";
  const controlBase = "w-full h-11 rounded-2xl bg-transparent px-4 text-sm leading-5 outline-none transition";

  const normalizeOption = (opt) => {
    if (typeof opt === "string" || typeof opt === "number") {
      return { label: String(opt), value: String(opt) };
    }
    if (opt && typeof opt === "object") {
      const v =
        opt.value ?? opt.id ?? opt.key ?? opt.label ?? opt.name ?? "";
      const l = opt.label ?? opt.name ?? String(v);
      return { label: String(l), value: String(v), disabled: Boolean(opt.disabled) };
    }
    return { label: "", value: "" };
  };

  // choose between input and select based on type
  const renderInput = () => {
    if (onControlClick) {
      return (
        <button
          type="button"
          onClick={onControlClick}
          disabled={disabled}
          aria-label={label}
          aria-haspopup={controlAriaHaspopup}
          aria-expanded={controlAriaExpanded}
          className={[
            controlBase,
            inputPadRight,
            inputText,
            "flex items-center gap-2 text-left",
            disabled ? "cursor-not-allowed opacity-60" : "hover:bg-neutral-50 dark:hover:bg-neutral-800/60",
          ].join(" ")}
        >
          {controlIcon}
          <span className={value ? "truncate" : "truncate text-neutral-400 dark:text-neutral-500"}>
            {value || placeholder}
          </span>
        </button>
      );
    }

    if (canUseSelect) {
      return (
        <ShopSelect
          value={value ?? ""}
          onValueChange={(nextValue) => onChange?.({ target: { value: nextValue } })}
          options={options.map(normalizeOption)}
          placeholder={placeholder}
          disabled={disabled || readOnly}
          ariaLabel={label}
          variant="ghost"
          hideIndicator
          invalid={Boolean(error)}
          triggerClassName={[
            controlBase,
            selectPadRight,
            inputText,
            disabled ? "opacity-60" : "",
          ].join(" ")}
        />
      );
    }

    return (
      <input
        value={value ?? ""}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        type={type}
        inputMode={inputMode}
        maxLength={maxLength}
        readOnly={readOnly}
        disabled={disabled}
        style={{ colorScheme: isDark ? "dark" : "light" }}
        className={
          controlBase +
          " " +
          inputPadRight +
          " " +
          inputText +
          " " +
          placeholderText +
          " " +
          (disabled ? "opacity-60" : "")
        }
      />
    );
  };

  return (
    <div>
      <div className="mb-1">
        <label className="text-xs font-semibold tracking-[0.20em] text-neutral-600 dark:text-neutral-400">
          {label}
        </label>
      </div>

      <div className={"relative rounded-2xl border transition " + border + " " + containerBg + " " + focusRing}>
        {renderInput()}

        {canUseSelect ? (
          <div
            className={
              "pointer-events-none absolute top-1/2 -translate-y-1/2 " +
              arrowColor +
              " " +
              selectArrowRightClass
            }
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        ) : null}

        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
          {secondaryActionLabel ? (
            <button
              type="button"
              disabled={secondaryActionDisabled}
              onClick={onSecondaryAction}
              className={btnBase + " " + btnGhost + " disabled:opacity-50"}
            >
              {secondaryActionLabel}
            </button>
          ) : null}

          {actionLabel ? (
            <button
              type="button"
              disabled={actionDisabled || actionLoading}
              onClick={onAction}
              className={
                btnBase +
                " " +
                btnPrimary +
                " disabled:opacity-50"
              }
            >
              {actionLoading ? "SAVING…" : actionLabel}
            </button>
          ) : null}
        </div>
      </div>

      {(error || helper) && (
        <div className={"mt-1 text-[11px] " + helperColor}>
          {error ? error : helper}
        </div>
      )}
    </div>
  );
}
